/**
 * Input adapter for the regulatory feasibility screen (R2).
 *
 * `regulatoryFeasibilityInputs({ intent, sed, coverageManifest })` derives the
 * minimal screen input from the frozen Project Intent, the SED and the
 * report's frozen coverage manifest. Read-only: it never mutates the intent or
 * the SED and adds nothing to either. Each input keeps its own provenance:
 *
 *   - jurisdiction: `regulatory.data.state` with `sedStateOrigin` (a
 *     bounding-box estimate stays 'estimate'); the local authority is only a
 *     geographic candidate because the jurisdiction screen always records
 *     `authorityScope: 'not-evaluated'`;
 *   - land use: Regrid parcel zoning/land-use labels, plus the zoning
 *     requirement's document-extracted ('doc') or user-authored ('user')
 *     fields from the coverage manifest (never its free-text notes). The
 *     record is a document only when its permitted-use list is
 *     document-extracted; free-text prohibitions are never turned into
 *     documented restrictions here;
 *   - air: the EPA Green Book reading (`readEpaNonattainment`) and Ozone
 *     Transport Region membership (`readOtrMembership`);
 *   - serving utility: phase `gridUtilityName` (declared) and the utility
 *     named by a utility letter/document (SED `grid.utilityName` only when a
 *     document backs it, else the coverage manifest's document-tier
 *     utility-letter field). AI research, a DAM baseline, the
 *     nearest-substation, transmission-line owner or operator is never used;
 *   - per-phase connection, technology, duty and MW; duty, export mode and
 *     electricity supply only from the R3 intent fields, else 'unknown'. A
 *     capacity factor, operating hours, sponsor archetype or PPA structure
 *     never infers any of them. `fieldSource[...] === 'user-unknown'` is
 *     carried as recorded-unknown.
 */
import type { CoverageManifestAtGen } from '../coverage/evidenceConsolidation';
import type { EvidenceSection, SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ProjectIntent, ProjectPhase } from '../../types/siteRag';
import { isVerifiedGridSection } from '../gridClaimHonesty';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import { normaliseStateToCode, sedStateOrigin } from '../inferSiteState';
import { readEpaNonattainment, readOtrMembership } from '../stage2/stage2Evidence';
import { epaDesignationAsOf } from '../sed/epaDesignation';
import {
  GENERATION_DUTIES, COMBUSTION_COMPONENTS, connectionScenarioFor, regulatoryFeasibilityInputSchema,
  type GenerationDuty, type RegulatoryFeasibilityInput, type RegulatoryPhaseInput,
} from './types';

// The R3 intent union and the screen vocabulary must stay identical.
const DUTY_PARITY: readonly NonNullable<ProjectPhase['generationDuty']>[] = GENERATION_DUTIES;
const DUTY_PARITY_REVERSE: readonly GenerationDuty[] = [] as NonNullable<ProjectPhase['generationDuty']>[];
void DUTY_PARITY; void DUTY_PARITY_REVERSE;

export interface RegulatoryFeasibilityInputSources {
  intent: ProjectIntent | null | undefined;
  sed: SiteEvidenceDocument | null | undefined;
  coverageManifest?: Pick<CoverageManifestAtGen, 'entries'> | null;
}

const PLACEHOLDER = /^(?:unknown|n\/?a|none|not available|tbd|-+)$/i;

/** Trimmed, non-placeholder text, else null. */
function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 && !PLACEHOLDER.test(trimmed) ? trimmed : null;
}

/** ISO calendar date at the start of a timestamp, else null. */
function dateOf(value: unknown): string | null {
  return typeof value === 'string' ? /^(\d{4}-\d{2}-\d{2})/.exec(value)?.[1] ?? null : null;
}

function sectionProvenance(section: EvidenceSection<unknown> | null | undefined, field: string): 'provider' | 'document' | 'derived' | 'none' {
  if (!section?.data || section.status === 'unavailable' || section.status === 'anomalous') return 'none';
  if (section.status === 'verified-from-doc' || section.docFieldSources?.[field] !== undefined) return 'document';
  return section.status === 'verified' ? 'provider' : 'derived';
}

function jurisdiction(sed: SiteEvidenceDocument | null | undefined): RegulatoryFeasibilityInput['jurisdiction'] {
  const regulatory = sed?.regulatory;
  const code = regulatory?.status === 'unavailable' ? null : normaliseStateToCode(regulatory?.data?.state);
  const origin = code ? sedStateOrigin(regulatory) : null;
  const local = regulatory?.status === 'unavailable' ? undefined : regulatory?.data?.jurisdiction;
  const candidateName = local
    ? text(local.governingJurisdiction) ?? text(local.incorporatedPlace?.name) ?? text(local.municipalities?.[0]) ?? text(local.etjAuthorities?.[0])
    : null;
  return {
    state: code && origin ? { value: code, provenance: origin } : { value: null, provenance: 'none' },
    localAuthority: {
      candidateName,
      screeningStatus: local ? member(local.status, ['inside-city-limits', 'inside-etj', 'outside-known-etj', 'mixed', 'unknown'] as const, 'unknown') : 'not-screened',
      authorityEvaluated: false,
    },
  };
}

function parcelLabel(sed: SiteEvidenceDocument | null | undefined, field: 'zoning' | 'landUse') {
  const section = sed?.parcel;
  const value = text(section?.data?.[field]);
  const provenance = value ? sectionProvenance(section, field) : 'none';
  return provenance === 'none'
    ? { value: null, provenance: 'none' as const, asOf: null }
    : { value, provenance, asOf: dateOf(section?.dataAsOf ?? section?.retrievedAt) };
}

type CoverageField = CoverageManifestAtGen['entries'][number]['fields'][number];
/** Document-extracted or user-authored fields of one requirement. Free-text
 * notes are not a field of the requirement and never create a record. */
function authoredFields(coverage: RegulatoryFeasibilityInputSources['coverageManifest'], requirementId: string): CoverageField[] {
  const entry = (coverage?.entries ?? []).find(row => row.requirementId === requirementId);
  return (entry?.fields ?? []).filter(field => (field.sourceTier === 'doc' || field.sourceTier === 'user') && field.key !== 'notes');
}

function listOf(value: unknown): string[] {
  const items = Array.isArray(value) ? value : typeof value === 'string' ? value.split(/[,;\n]/) : [];
  return items.map(text).filter((item): item is string => item !== null);
}

function finiteNonNegative(value: unknown): number | null {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof number === 'number' && Number.isFinite(number) && number >= 0 ? number : null;
}

function zoningDocument(coverage: RegulatoryFeasibilityInputSources['coverageManifest']): RegulatoryFeasibilityInput['landUse']['zoningDocument'] {
  const fields = authoredFields(coverage, 'zoning');
  if (fields.length === 0) return null;
  const field = (key: string) => fields.find(candidate => candidate.key === key);
  const value = (key: string) => field(key)?.value;
  return {
    // Provenance is per field (an authored value beats a document value), so
    // the record is a document only when the permitted-use list and any
    // special conditions both come from a document: an authored condition can
    // replace the document's stated requirement.
    tier: field('permittedUses')?.sourceTier === 'doc'
      && (field('specialConditions') === undefined || field('specialConditions')?.sourceTier === 'doc')
      ? 'document' : 'declared',
    zoningCode: text(value('zoningCode')),
    permittedUses: listOf(value('permittedUses')),
    setbacksFt: finiteNonNegative(value('setbacks_ft')),
    specialConditions: text(value('specialConditions')),
  };
}

function epa(sed: SiteEvidenceDocument | null | undefined): RegulatoryFeasibilityInput['air']['epa'] {
  const data = sed?.environmental?.data?.naaqsDesignations;
  const signal = readEpaNonattainment(data);
  if (signal === undefined) return { status: 'not-assessed' };
  if (signal === null) {
    const asOf = dateOf(epaDesignationAsOf(data));
    return asOf ? { status: 'not-listed', asOf } : { status: 'not-assessed' };
  }
  return {
    status: 'nonattainment',
    asOf: dateOf(signal.epa?.asOf),
    pollutant: signal.pollutant,
    classification: signal.classification,
    siteContainment: signal.epa?.siteContainment ?? (signal.epa?.partOfCounty ? 'unknown' : 'whole-county'),
  };
}

const ISO_NAMES = new Set(['PJM', 'MISO', 'ERCOT', 'CAISO', 'NYISO', 'ISO-NE', 'SPP']);

/** A section touched by a document does not prove every field came from it.
 * Name only a field-bound document witness; broad section provenance stays
 * context and can never supply the serving-utility authority label. */
function documentedSedUtilityName(grid: SiteEvidenceDocument['grid'] | undefined): string | null {
  const name = text(grid?.data?.utilityName);
  if (!grid || !name || grid.status === 'unavailable' || grid.status === 'anomalous') return null;
  const source = grid.docFieldSources?.utilityName;
  return source?.docId && text(String(source.value)) === name
    && typeof source.excerptPreview === 'string' && source.excerptPreview.includes(name)
    && source.attribution?.appliesToProject !== 'other-project' ? name : null;
}

function utility(intent: ProjectIntent | null | undefined, sed: SiteEvidenceDocument | null | undefined,
  coverage: RegulatoryFeasibilityInputSources['coverageManifest']): RegulatoryFeasibilityInput['utility'] {
  const declared = [...new Set((intent?.phases ?? [])
    .filter(phase => phase.powerSourceConfig?.gridConnection !== 'islanded')
    .map(phase => text(phase.powerSourceConfig?.gridUtilityName))
    .filter((name): name is string => name !== null))];
  const grid = sed?.grid?.status === 'unavailable' ? undefined : sed?.grid;
  // Only a utility letter/document names the serving utility. Substation,
  // transmission-line owner and operator fields are deliberately not read.
  const sedUtility = documentedSedUtilityName(grid);
  const letterUtility = text(authoredFields(coverage, 'utility-letter').find(field => field.key === 'utilityName' && field.sourceTier === 'doc')?.value);
  const documented = sedUtility
    ? { name: sedUtility, asOf: dateOf(grid?.dataAsOf ?? grid?.retrievedAt) }
    : letterUtility ? { name: letterUtility, asOf: null } : null;
  const isoValue = [sed?.interconnection?.data?.iso, grid?.data?.isoRegion]
    .map(value => text(value)?.toUpperCase() ?? null)
    .find((value): value is string => value !== null && ISO_NAMES.has(value)) ?? null;
  return {
    declaredServingUtilities: declared,
    documentedServingUtility: documented,
    iso: isoValue ? { value: isoValue, provenance: 'provider' } : { value: null, provenance: 'none' },
  };
}

/** A persisted value, or the fallback when it is not one of the allowed members. */
function member<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? value as T : fallback;
}

function phases(intent: ProjectIntent | null | undefined): RegulatoryPhaseInput[] {
  const seen = new Set<string>();
  return [...(intent?.phases ?? [])]
    .sort((a, b) => a.sequence - b.sequence)
    .map((phase, index): RegulatoryPhaseInput => {
      const config = phase.powerSourceConfig;
      const gridConnectionDeclared = config ? member(config.gridConnection, ['islanded', 'grid-connected', 'undecided'] as const, 'undecided') : 'absent';
      const exportModeDeclared = member(config?.exportMode, ['non-export', 'export', 'unknown'] as const, 'absent');
      const components = [...new Set((config?.components ?? []).map(text).filter((component): component is string => component !== null))];
      // Unique, non-empty ids (a malformed legacy phase never aborts the screen).
      let phaseId = text(phase.id) ?? `phase-${index + 1}`;
      if (seen.has(phaseId)) phaseId = `${phaseId}-${index + 1}`;
      seen.add(phaseId);
      return {
        phaseId,
        sequence: Number.isInteger(phase.sequence) && phase.sequence > 0 ? phase.sequence : index + 1,
        gridConnectionDeclared,
        exportModeDeclared,
        connection: connectionScenarioFor(gridConnectionDeclared, exportModeDeclared),
        connectionRecordedUnknown: isIntentFieldExplicitlyUnknown(intent, `phases.${phase.id}.powerSourceConfig`)
          || isIntentFieldExplicitlyUnknown(intent, `phases.${phase.id}.powerSourceConfig.exportMode`),
        components,
        combustion: components.some(component => COMBUSTION_COMPONENTS.has(component)),
        generation: components.some(component => component !== 'grid'),
        duty: member(phase.generationDuty, GENERATION_DUTIES, 'unknown'),
        dutyRecordedUnknown: isIntentFieldExplicitlyUnknown(intent, `phases.${phase.id}.generationDuty`),
        capacityMW: finiteNonNegative(phase.capacityMW),
      };
    });
}

function gridService(intent: ProjectIntent | null | undefined, sed: SiteEvidenceDocument | null | undefined): RegulatoryFeasibilityInput['gridService'] {
  const grid = sed?.grid;
  const evidence = isVerifiedGridSection(grid) ? grid.data : grid?.status === 'verified-from-doc' ? grid.data : null;
  const detail = evidence?.capacityDetail;
  const basis = member(detail?.basis, ['utility-indication', 'study', 'executed'] as const, 'none');
  const mw = finiteNonNegative(detail?.mw);
  const capacity = basis !== 'none' && mw !== null
    ? { basis, mw, asOf: dateOf(grid?.dataAsOf ?? grid?.retrievedAt), interconnectionClass: text(evidence?.interconnectionClass) }
    : { basis: 'none' as const, mw: null, asOf: null, interconnectionClass: null };
  return {
    capacity,
    loadServiceStatus: {
      value: member(intent?.interconnectionStatus?.queueStatus, ['none', 'pre-application', 'queued', 'study', 'executed-LGIA', 'executed-load-service'] as const, 'unknown'),
      recordedUnknown: isIntentFieldExplicitlyUnknown(intent, 'interconnectionStatus'),
    },
  };
}

/** Pure derivation of the screen input. Throws only on a contract bug. */
export function regulatoryFeasibilityInputs(sources: RegulatoryFeasibilityInputSources): RegulatoryFeasibilityInput {
  const { intent, sed, coverageManifest } = sources;
  return regulatoryFeasibilityInputSchema.parse({
    schemaVersion: 1,
    jurisdiction: jurisdiction(sed),
    landUse: {
      parcelZoning: parcelLabel(sed, 'zoning'),
      parcelLandUse: parcelLabel(sed, 'landUse'),
      zoningDocument: zoningDocument(coverageManifest),
      // No structured, rule-status-bearing restriction source exists yet; a
      // prohibition in free text is never promoted to a confirmed restriction.
      documentedRestrictions: [],
    },
    air: { epa: epa(sed), ozoneTransportRegion: readOtrMembership(sed).status },
    utility: utility(intent, sed, coverageManifest),
    phases: phases(intent),
    fuel: {
      supply: member(intent?.fuelAccess?.supply, ['none', 'near-pipeline', 'lateral-required', 'firm-contracted'] as const, 'unknown'),
      recordedUnknown: isIntentFieldExplicitlyUnknown(intent, 'fuelAccess'),
    },
    electricitySupply: {
      arrangement: member(intent?.electricitySupply?.arrangement, ['own-use', 'third-party-onsite-sale', 'utility-supplied'] as const, 'unknown'),
      sellerName: text(intent?.electricitySupply?.sellerName),
      recordedUnknown: isIntentFieldExplicitlyUnknown(intent, 'electricitySupply'),
    },
    gridService: gridService(intent, sed),
  } satisfies RegulatoryFeasibilityInput);
}
